import * as RTabs from "@radix-ui/react-tabs";
import { type ReactElement, type ReactNode } from "react";

import { cn } from "../utils/cn";

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: readonly TabItem[];
  defaultValue?: string | undefined;
  value?: string | undefined;
  onValueChange?: ((value: string) => void) | undefined;
  className?: string | undefined;
  /** Sit the triggers on a bottom-border rail (like the prototype's Foundations page). */
  underline?: boolean | undefined;
  "aria-label"?: string | undefined;
}

export function Tabs({
  items,
  defaultValue,
  value,
  onValueChange,
  className,
  underline = true,
  ...aria
}: TabsProps): ReactElement {
  const initial = defaultValue ?? items[0]?.value;
  return (
    <RTabs.Root
      {...(value !== undefined && { value })}
      {...(initial !== undefined && { defaultValue: initial })}
      {...(onValueChange !== undefined && { onValueChange })}
      className={cn("w-full", className)}
    >
      <RTabs.List
        aria-label={aria["aria-label"] ?? "Tabs"}
        className={cn(
          "flex gap-1",
          underline && "border-b border-border-default",
        )}
      >
        {items.map((it) => (
          <RTabs.Trigger
            key={it.value}
            value={it.value}
            {...(it.disabled !== undefined && { disabled: it.disabled })}
            className={cn(
              "-mb-px cursor-pointer border-b-2 border-transparent px-4.5 py-3 text-sm font-medium text-neutral-500 transition-colors",
              "hover:text-neutral-800",
              "data-[state=active]:border-primary-500 data-[state=active]:font-semibold data-[state=active]:text-primary-700",
              "disabled:cursor-default disabled:opacity-50",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
            )}
          >
            {it.label}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {items.map((it) => (
        <RTabs.Content
          key={it.value}
          value={it.value}
          className="pt-8 focus-visible:outline-none"
        >
          {it.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  );
}
